import { DT } from '../constants';
import type { BroodDef, EnemyDef } from '../defs';
import type { World } from '../World';
import { spawnEnemyAt } from './levelSystem';

/** Invocation ACTIVE d'un ennemi : celle de sa PHASE de combat courante si elle
 *  en porte une, sinon celle de base (`def.brood`). C'est ce qui permet à un boss
 *  d'appeler des renforts DIFFÉRENTS selon la phase — ou de n'en appeler que dans
 *  une phase donnée. `phaseSystem` remet les compteurs à zéro à l'entrée d'une
 *  phase à invocation, donc chaque phase pond à neuf. */
export function activeBrood(def: EnemyDef, phase: number): BroodDef | undefined {
  if (phase > 0 && def.phases && phase <= def.phases.length) {
    const b = def.phases[phase - 1]!.brood;
    if (b) return b;
  }
  return def.brood;
}

/* ═══════════════════════════════════════════════════════════════════════════
   PONTE et ÉCLOSION — un ennemi en engendre un autre en cours de combat.

   C'est la seule VRAIE capacité moteur neuve de cette passe. Jusqu'ici, seul
   `levelSystem` faisait apparaître des ennemis, à partir de la timeline. Ici un
   PONDEUR pose des œufs, et un ŒUF éclôt en larve — tout en réutilisant le même
   chemin de spawn (`spawnEnemyAt`) que les vagues, donc le télégraphe, la
   collision, les émetteurs et le butin s'appliquent sans un cas particulier.

   ── Où il tourne dans `step` ───────────────────────────────────────────────
   Juste après le peuplement de vague (`levelSystem`, `spawnSystem`) et AVANT
   que les ennemis n'agissent (`enemyBulletSystem`, `emitterSystem`). Un engendré
   né ce tick-ci porte donc son télégraphe : il n'est ni touchable ni dangereux
   avant de s'être montré, exactement comme un ennemi de vague.

   ── Les deux bornes, non négociables ───────────────────────────────────────
   `brood.max` plafonne les éclosions par porteur — un pondeur non borné sature
   les 256 slots du pool et le budget de frame. Et la CHAÎNE termine sur une
   feuille (la larve n'a pas de `brood`) : sans feuille, l'engendrement se
   propagerait sans fin. La saturation du pool est gérée sans planter : un spawn
   refusé est compté en overflow par `spawnEnemyAt`, le compteur n'avance pas, et
   la ponte est retentée au cycle suivant — jamais au-delà de la borne.

   ── Zéro allocation, itération EN ARRIÈRE ──────────────────────────────────
   Aucune structure créée par tick. La boucle à la fois LIBÈRE (un œuf disparaît
   en éclosant, `dieAfter`) et APPEND (un engendré est alloué au bout du pool).
   Les deux coexistent sans piège : `alloc` ne déplace aucun slot vivant
   existant, et l'auto-libération du slot courant en marche arrière est le motif
   sûr documenté dans `SoAPool`. La borne de boucle `en.count - 1` est figée à
   l'entrée : les engendrés apparus au bout ne sont pas visités ce tick — ce qui
   est exactement voulu, ils portent leur télégraphe.
   ═══════════════════════════════════════════════════════════════════════════ */

export function broodSystem(w: World): void {
  const en = w.enemies;

  for (let i = en.count - 1; i >= 0; i--) {
    const s = en.at(i);

    // Encore en apparition : le porteur ne pond pas tant qu'il n'est pas
    // installé, sinon l'horloge de ponte tournerait pendant le télégraphe et la
    // première éclosion tomberait avant même qu'on ait vu le pondeur.
    if (en.telegraph[s]! > 0) continue;

    const def = w.defs.enemies[en.kind[s]!];
    if (!def) continue;
    // Invocation de la PHASE courante (boss) si elle en a une, sinon celle de base.
    const brood = activeBrood(def, en.phase[s]!);
    if (!brood) continue;

    const done = en.broodDone[s]!;
    if (done >= brood.max) continue; // borne atteinte : plus rien à engendrer

    // Accumulateur de temps depuis la dernière ponte. La première tombe donc
    // `every` secondes après la fin du télégraphe.
    let cd = en.broodCd[s]! + DT;
    if (cd < brood.every) {
      en.broodCd[s] = cd;
      continue;
    }
    // On consomme le temps QUOI QU'IL ARRIVE (même en cas de refus de pool
    // ci-dessous) : sans quoi un pool saturé ferait retenter — donc compter un
    // overflow — à chaque tick jusqu'à la libération d'un slot.
    cd -= brood.every;
    en.broodCd[s] = cd;

    const idx = w.defs.enemyByI.get(brood.enemy);
    if (idx === undefined) {
      // Référence cassée : on arrête net plutôt que de boucler dans le vide. En
      // pratique le registre valide les références au chargement ; cette garde
      // couvre un index fabriqué à la main dans un test.
      en.broodDone[s] = brood.max;
      continue;
    }

    const x = en.x[s]!;
    const y = en.y[s]! + (brood.offY ?? 0);
    const child = spawnEnemyAt(w, idx, x, y);
    // Pool saturé : `spawnEnemyAt` a déjà compté l'overflow. On n'incrémente PAS
    // le compteur — la ponte manquée sera reprise au prochain cycle, sans jamais
    // dépasser la borne — et le porteur ne meurt pas d'une ponte qui n'a pas eu
    // lieu.
    if (child < 0) continue;

    const nd = done + 1;
    en.broodDone[s] = nd;

    // Un ŒUF éclôt une fois puis DISPARAÎT : le laisser en scène pondrait le
    // décor de coquilles vides. Un PONDEUR (`dieAfter` absent) persiste et
    // continue de menacer entre deux pontes.
    if (brood.dieAfter && nd >= brood.max) en.free(s);
  }
}
